import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import FloatingCTA from '../../components/feature/FloatingCTA';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const team = [
  {
    name: 'Brian M.',
    role: 'Founder & Lead Chimney Technician',
    certs: 'CSIA Certified Sweep',
    bio: 'Brian founded BBM Services in 2010 with a mission to bring certified, honest chimney and duct cleaning to Jacksonville homeowners. With over 15 years of hands-on experience, he leads every major inspection personally.',
    image: 'https://public.readdy.ai/ai/img_res/86c7b01c104371d09357786340cee712.jpg',
  },
  {
    name: 'Marcus R.',
    role: 'HVAC & Air Duct Specialist',
    certs: 'NADCA Certified',
    bio: 'Marcus specializes in air duct and HVAC cleaning with NADCA certification. He has cleaned over 1,200 duct systems across Jacksonville and is known for his thoroughness and attention to detail.',
    image: 'https://readdy.ai/api/search-image?query=professional%20male%20HVAC%20technician%20in%20uniform%20with%20equipment%2C%20confident%20pose%2C%20Jacksonville%20Florida%20service%20professional%2C%20clean%20background%20portrait%2C%20friendly%20and%20experienced%2C%2035%20years%20old&width=400&height=500&seq=team002&orientation=portrait',
  },
  {
    name: 'Tony K.',
    role: 'Dryer Vent & Safety Specialist',
    certs: 'CSIA Dryer Exhaust Technician',
    bio: 'Tony holds the CSIA Dryer Exhaust Technician certification and has helped hundreds of Jacksonville families eliminate dryer fire risks. He is passionate about home safety education.',
    image: 'https://readdy.ai/api/search-image?query=professional%20male%20appliance%20technician%20in%20work%20uniform%2C%20friendly%20confident%20smile%2C%20service%20professional%20portrait%2C%20clean%20studio%20background%2C%20experienced%20and%20approachable%2C%20mid%2030s&width=400&height=500&seq=team003&orientation=portrait',
  },
];

export default function AboutPage() {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <>
      <Header />
      <FloatingCTA />

      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-brand-dark overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=professional%20home%20service%20team%20in%20branded%20uniforms%20standing%20together%20in%20front%20of%20Jacksonville%20Florida%20suburban%20home%2C%20confident%20team%20photo%2C%20service%20van%20visible%2C%20clear%20sky%2C%20professional%20local%20business%2C%20warm%20friendly%20atmosphere&width=1600&height=700&seq=about-hero&orientation=landscape"
            alt="BBM Services team Jacksonville FL"
            className="w-full h-full object-cover object-top opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-dark/90 to-brand-dark/60" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 text-brand-amber text-xs font-bold uppercase tracking-widest mb-5">
            <div className="h-px w-8 bg-brand-amber" />
            About Us
            <div className="h-px w-8 bg-brand-amber" />
          </div>
          <h1 className="font-serif text-5xl md:text-6xl font-bold text-white mb-5">
            Jacksonville's Trusted Home Safety Cleaning Specialists
          </h1>
          <p className="text-white/70 text-xl">
            Certified, insured, and dedicated to protecting your home — since 2010.
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div ref={ref} className={`flex flex-col lg:flex-row gap-14 items-center transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="lg:w-1/2">
              <img
                src="https://readdy.ai/api/search-image?query=warm%20interior%20of%20Jacksonville%20Florida%20home%20with%20a%20clean%20beautiful%20fireplace%2C%20cozy%20family%20living%20room%2C%20warm%20lighting%2C%20clean%20chimney%20and%20fireplace%20after%20professional%20cleaning%2C%20inviting%20home%20atmosphere%2C%20premium%20residential%20interior&width=800&height=600&seq=about-story&orientation=landscape"
                alt="BBM Services chimney cleaning Jacksonville FL"
                className="w-full h-80 lg:h-[460px] object-cover object-top rounded-2xl"
              />
            </div>
            <div className="lg:w-1/2">
              <h2 className="font-serif text-4xl font-bold text-brand-dark mb-5">Our Story</h2>
              <p className="text-brand-warm-gray text-lg leading-relaxed mb-4">
                BBM Services was founded in Jacksonville in 2010 by Brian M., a CSIA-certified chimney sweep who saw firsthand how many local homeowners were at risk from neglected chimneys, clogged dryer vents, and contaminated air ducts — and simply didn't know it.
              </p>
              <p className="text-brand-warm-gray text-base leading-relaxed mb-4">
                Starting with just one service van and a commitment to honest, professional work, Brian grew BBM Services into one of Jacksonville's most trusted names in home safety cleaning. Today, our team of certified technicians serves thousands of families across Jacksonville and the surrounding region.
              </p>
              <p className="text-brand-warm-gray text-base leading-relaxed mb-8">
                We believe every Jacksonville homeowner deserves to know that their chimney is clean, their air is safe, and their dryer vent isn't a fire hazard waiting to happen. That's what we do. Every day.
              </p>
              <div className="flex flex-wrap gap-4">
                {['CSIA Certified', 'NADCA Member', 'Fully Insured', 'BBB Accredited'].map((badge) => (
                  <div key={badge} className="flex items-center gap-2 bg-amber-50 border border-amber-100 px-4 py-2 rounded-lg">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-verified-badge-fill text-brand-amber text-sm" />
                    </div>
                    <span className="text-brand-dark text-sm font-semibold">{badge}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-20 bg-brand-muted">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="font-serif text-4xl font-bold text-brand-dark mb-3">Meet Our Team</h2>
            <p className="text-brand-warm-gray text-lg max-w-xl mx-auto">Certified professionals who show up, do the job right, and genuinely care about your home's safety.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {team.map((member) => (
              <div key={member.name} className="bg-white rounded-2xl overflow-hidden border border-stone-200">
                <div className="h-72 overflow-hidden">
                  <img src={member.image} alt={member.name} className="w-full h-full object-cover object-top" />
                </div>
                <div className="p-6">
                  <h3 className="font-serif text-xl font-bold text-brand-dark mb-1">{member.name}</h3>
                  <div className="text-brand-amber text-sm font-semibold mb-1">{member.role}</div>
                  <div className="text-xs text-brand-warm-gray bg-amber-50 px-2.5 py-1 rounded-full inline-block mb-3">{member.certs}</div>
                  <p className="text-brand-warm-gray text-sm leading-relaxed">{member.bio}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-brand-amber">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-4">Ready to Work With Jacksonville's Best?</h2>
          <p className="text-white/80 text-lg mb-7">Get a free quote today — same-week appointments available.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="bg-white text-brand-amber font-bold py-3.5 px-8 rounded-lg text-base cursor-pointer whitespace-nowrap hover:bg-amber-50 transition-all">Book Service</Link>
            <a href="tel:+19045550191" className="border-2 border-white/40 text-white font-semibold py-3.5 px-8 rounded-lg text-base cursor-pointer whitespace-nowrap hover:border-white transition-all">Call (904) 555-0191</a>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
